import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { Settings2, ZapOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '@/api/client';
import { useGateways, useTestGateway, useUpsertGateway } from '@/api/queries';
import type { PspCode } from '@/api/types';
import { ErrorBox } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { PspBrand } from './psp-brand';
import { ALL_PSPS, PSP_SCHEMAS } from './psp-schemas';

type FormState = { psp: PspCode; open: boolean };

export function GatewaysPanel({ merchantId }: { merchantId: string }) {
  const { t } = useTranslation();
  const gateways = useGateways(merchantId);
  const upsert = useUpsertGateway(merchantId);
  const test = useTestGateway(merchantId);
  const [editing, setEditing] = useState<FormState | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-base font-semibold">{t('gateways_panel.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('gateways_panel.description')}</p>
        </div>
      </div>

      {gateways.isError && <ErrorBox message={t('common.error')} />}

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('gateways_panel.psp_label')}</TableHead>
              <TableHead>{t('gateways_panel.env_label')}</TableHead>
              <TableHead>{t('gateways_panel.active_label')}</TableHead>
              <TableHead>v</TableHead>
              <TableHead>{t('gateways_panel.payload_label')}</TableHead>
              <TableHead>{t('common.updated')}</TableHead>
              <TableHead className="w-40" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {ALL_PSPS.map((psp) => {
              const row = gateways.data?.find((g) => g.psp_code === psp && g.active);
              return (
                <TableRow key={psp}>
                  <TableCell><PspBrand psp={psp} /></TableCell>
                  <TableCell>{row?.env ?? '—'}</TableCell>
                  <TableCell>
                    {row ? (
                      <Badge variant={row.active ? 'success' : 'outline'}>
                        {row.active
                          ? t('gateways_panel.active_label')
                          : t('gateways_panel.inactive_label')}
                      </Badge>
                    ) : (
                      <Badge variant="outline">{t('common.not_set')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{row?.version ?? '—'}</TableCell>
                  <TableCell className="text-xs">
                    {row?.payload_keys.length ? (
                      row.payload_keys.map((k) => (
                        <Badge key={k} variant="secondary" className="me-1">
                          {k}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {row ? dayjs(row.updated_at).format('YYYY-MM-DD HH:mm') : '—'}
                  </TableCell>
                  <TableCell className="text-end space-x-1">
                    {row && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={test.isPending}
                        onClick={() =>
                          test.mutate(psp, {
                            onSuccess: (r) =>
                              r.ok
                                ? toast.success(`${t('gateways_panel.test_passed')}: ${r.detail}`)
                                : toast.error(`${t('gateways_panel.test_failed')}: ${r.detail}`),
                            onError: (e) =>
                              toast.error(
                                e instanceof ApiError
                                  ? e.message
                                  : (t('gateways_panel.test_failed') as string),
                              ),
                          })
                        }
                      >
                        <ZapOff className="h-3.5 w-3.5" /> {t('gateways_panel.test')}
                      </Button>
                    )}
                    <Button size="sm" onClick={() => setEditing({ psp, open: true })}>
                      <Settings2 className="h-3.5 w-3.5" />{' '}
                      {row ? t('common.update') : t('common.create')}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {editing && (
        <UpsertDialog
          merchantId={merchantId}
          psp={editing.psp}
          open={editing.open}
          onOpenChange={(open) => setEditing((s) => (s ? { ...s, open } : null))}
          onSubmit={async (payload, base_url) => {
            await new Promise<void>((resolve, reject) => {
              upsert.mutate(
                {
                  psp: editing.psp,
                  body: {
                    psp: editing.psp,
                    env: 'prod',
                    payload,
                    base_url: base_url || null,
                    active: true,
                  },
                },
                {
                  onSuccess: () => {
                    toast.success(t('gateways_panel.saved_toast') as string);
                    setEditing(null);
                    resolve();
                  },
                  onError: (e) => {
                    toast.error(
                      e instanceof ApiError ? e.message : (t('common.failed') as string),
                    );
                    reject(e);
                  },
                },
              );
            });
          }}
        />
      )}
    </div>
  );
}

function UpsertDialog({
  merchantId,
  psp,
  open,
  onOpenChange,
  onSubmit,
}: {
  merchantId: string;
  psp: PspCode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: Record<string, unknown>, baseUrl?: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const def = PSP_SCHEMAS[psp];
  const form = useForm({
    resolver: zodResolver(def.schema),
    defaultValues: def.defaults as Record<string, unknown>,
  });
  const [baseUrl, setBaseUrl] = useState('');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={form.handleSubmit(async (values) => {
            const payload = Object.fromEntries(
              Object.entries(values).filter(([, v]) => v !== '' && v !== undefined),
            );
            await onSubmit(payload, baseUrl.trim() || undefined);
          })}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PspBrand psp={psp} />
              <span className="text-muted-foreground font-normal">· {merchantId.slice(0, 8)}</span>
            </DialogTitle>
            <DialogDescription>{t('gateways_panel.description')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {def.fields.map((f) => (
              <div key={f.name} className="space-y-1.5">
                <Label htmlFor={f.name}>{f.label}</Label>
                {f.kind === 'select' && f.options ? (
                  <Select
                    value={(form.watch(f.name) as string | undefined) ?? ''}
                    onValueChange={(v) => form.setValue(f.name, v as never, { shouldValidate: true })}
                  >
                    <SelectTrigger id={f.name}>
                      <SelectValue placeholder={f.placeholder ?? '—'} />
                    </SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={f.name}
                    type={f.kind === 'secret' ? 'password' : f.kind === 'number' ? 'number' : 'text'}
                    placeholder={f.placeholder}
                    autoComplete="off"
                    {...form.register(f.name)}
                  />
                )}
                {f.help && <p className="text-xs text-muted-foreground">{f.help}</p>}
                {form.formState.errors[f.name] && (
                  <p className="text-xs text-destructive">
                    {String(form.formState.errors[f.name]?.message ?? '')}
                  </p>
                )}
              </div>
            ))}

            <div className="space-y-1.5 border-t border-border pt-3">
              <Label htmlFor="base_url">
                {t('gateways_panel.base_url_label')} ({t('common.optional')})
              </Label>
              <Input
                id="base_url"
                placeholder="https://sandbox.psp.example"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
